import { z } from 'zod';
import { PLAYLIST_CONSTRAINTS } from '@/lib/constants/playlist';
import { REF_ID_PATTERN } from '@/lib/constants/media';

/** Settings form schema (shared by both playlist types) */
export const PlaylistSettingsSchema = z.object({
  title: z.string()
    .min(PLAYLIST_CONSTRAINTS.TITLE_MIN, 'Title is required')
    .max(PLAYLIST_CONSTRAINTS.TITLE_MAX, `Title cannot exceed ${PLAYLIST_CONSTRAINTS.TITLE_MAX} characters`),
  descShort: z.string()
    .max(PLAYLIST_CONSTRAINTS.DESC_SHORT_MAX, `Description cannot exceed ${PLAYLIST_CONSTRAINTS.DESC_SHORT_MAX} characters`),
  refId: z.string()
    .max(PLAYLIST_CONSTRAINTS.REF_ID_MAX, `Reference ID cannot exceed ${PLAYLIST_CONSTRAINTS.REF_ID_MAX} characters`)
    .refine(v => !v || REF_ID_PATTERN.test(v), 'Only alphanumeric characters, hyphens, and underscores allowed'),
  mediaStateType: z.enum(['active', 'inactive']),
});

export type PlaylistSettingsFormValues = z.infer<typeof PlaylistSettingsSchema>;

/** Tag rules content form schema (smart playlists only) */
export const TagContentSchema = z.object({
  tagRules: z.array(z.object({
    lop: z.enum(['and', 'or', 'not']),
    values: z.array(z.string()),
  })).min(1, 'At least one tag rule is required')
    .max(PLAYLIST_CONSTRAINTS.MAX_TAG_RULES),
  sortUIType: z.string().min(1, 'Sort order is required'),
  limit: z.string().refine(
    v => v === '' || (Number.isInteger(Number(v)) && Number(v) > 0 && Number(v) <= PLAYLIST_CONSTRAINTS.MAX_LIMIT),
    { message: `Limit must be 1-${PLAYLIST_CONSTRAINTS.MAX_LIMIT} or empty for no limit` }
  ),
});

export type TagContentFormValues = z.infer<typeof TagContentSchema>;

/** Smart playlist unified form schema (settings + tag content combined) */
export const SmartPlaylistFormSchema = PlaylistSettingsSchema.merge(TagContentSchema);
export type SmartPlaylistFormValues = z.infer<typeof SmartPlaylistFormSchema>;

/** Create playlist form schema */
export const CreatePlaylistSchema = z.object({
  title: z.string()
    .min(PLAYLIST_CONSTRAINTS.TITLE_MIN, 'Title is required')
    .max(PLAYLIST_CONSTRAINTS.TITLE_MAX),
  listType: z.enum(['custom', 'tag']),
  mediaType: z.string().min(1, 'Media type is required'),
});

export type CreatePlaylistFormValues = z.infer<typeof CreatePlaylistSchema>;
